// istanbul ignore file — composition shell; logic lives in hooks and child modules.

import { showError, showSuccess } from '@calendly/alert';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { Body, Footer, Popup } from '@calendly/ui/components/popup';
import { SwitchInput } from '@calendly/ui/components/switch-input';
import { TitleText, BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { forwardRef, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { GMAIL_SYNC_ALL_MEMBERS, GMAIL_SYNC_NO_ONE, useOrganizationPreferenceQuery, useUpsertOrganizationPreferenceMutation } from 'features/crm';
const GRAPHQL_ERRORS_PROP = '_graphqlErrors';
const rowCss = "r118h65p";
const Row = forwardRef((props, ref) => <div ref={ref} {...props} className={classNames(rowCss, props.className)} />);
const GoogleSyncConfirmationPopup = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading
}) => <Popup isOpen={isOpen} onClose={onClose} title={I18n.t('publisher.admin.integrations.google_sync.confirm.disable_title')}>
    <Body>
      {HtmlParser(I18n.t('publisher.admin.integrations.google_sync.confirm.disable_body'))}
    </Body>
    <Footer>
      <Button decoration="secondary-outline" onClick={onClose} disabled={isLoading}>
        {I18n.t('publisher.action.cancel')}
      </Button>
      <Button decoration="danger" disabled={isLoading} loading={isLoading} onClick={onConfirm}>
        {I18n.t('publisher.admin.integrations.google_sync.confirm.disable_confirm_button')}
      </Button>
    </Footer>
  </Popup>;
export const GoogleSyncSection = () => {
  const dispatch = useDispatch();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const {
    data,
    isFetching: isQueryFetching
  } = useOrganizationPreferenceQuery();
  const [upsertOrganizationPreference, {
    isLoading: isMutationLoading
  }] = useUpsertOrganizationPreferenceMutation();
  const gmailSyncEnabledFor = data?.organizationPreference?.gmailSyncEnabledFor ?? GMAIL_SYNC_ALL_MEMBERS;
  const isEnabled = gmailSyncEnabledFor === GMAIL_SYNC_ALL_MEMBERS;
  const applyGoogleSync = useCallback(async gmailSyncEnabledForValue => {
    try {
      await upsertOrganizationPreference({
        gmailSyncEnabledFor: gmailSyncEnabledForValue
      }).unwrap();
      dispatch(showSuccess(I18n.t('publisher.flash.settings_saved')));
      setIsPopupOpen(false);
    } catch (error) {
      const graphqlMessage = error?.data?.[GRAPHQL_ERRORS_PROP]?.[0]?.message ?? error?.error;
      dispatch(showError(graphqlMessage || I18n.t('publisher.flash.error')));
      setIsPopupOpen(false);
    }
  }, [dispatch, upsertOrganizationPreference]);
  const handleToggleChange = useCallback(() => {
    if (isEnabled) {
      setIsPopupOpen(true);
    } else {
      applyGoogleSync(GMAIL_SYNC_ALL_MEMBERS);
    }
  }, [applyGoogleSync, isEnabled]);
  const handleConfirm = useCallback(() => applyGoogleSync(GMAIL_SYNC_NO_ONE), [applyGoogleSync]);
  const handleCancel = useCallback(() => {
    setIsPopupOpen(false);
  }, []);
  return <>
      <TitleText level={3} size="medium">
        {I18n.t('publisher.admin.integrations.google_sync.title')}
      </TitleText>
      <Row>
        <BodyText>
          {I18n.t('publisher.admin.integrations.google_sync.description')}
        </BodyText>
        <label>
          <SwitchInput checked={isEnabled} disabled={isQueryFetching || isMutationLoading} onChange={handleToggleChange} aria-label={I18n.t('publisher.admin.integrations.google_sync.title')} />
        </label>
      </Row>
      {isPopupOpen && <GoogleSyncConfirmationPopup isOpen={isPopupOpen} onClose={handleCancel} onConfirm={handleConfirm} isLoading={isMutationLoading} />}
    </>;
};
export const SettingsModalIntegrationsPage = () => <div data-testid="settings-modal-integrations-page">
    <GoogleSyncSection />
  </div>;

require("./settings_modal_integrations_page.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./settings_modal_integrations_page.js");